import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cx from 'classnames';
import translate from '../../../commonComponents/translate';
import OrderProp from '../propTypes/orderProp';
import SellerInfo from '../../orders/Components/SellerInfo';
import { compose, PAYMENT_METHOD } from '../../../services/utilityFunctions';
import TimelineWrapper from '../detail/components/TimelineWrapper';
import MondialRelayWrapper from '../detail/components/MondialRelayWrapper';
import Anchor from '../../../commonComponents/Anchor';
import TenantProp, {
  PAYMENT_PROVIDER,
} from '../../../commonComponents/propTypes/tenantProp';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import UpdateStatusContainer from '../components/UpdateStatusContainer';
import ChatService from '../../../services/chatService';
import Notifications from '../../../services/notifications';
import withCSRF from '../../../commonComponents/withCSRF';

function ShopSidebarOrder({
  t,
  _csrf,
  order,
  isAcademy,
  tenant,
  isOpportunity,
  onSaveOrder,
  isLoading,
}) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { paymentProviderType } = tenant?.settings || {};
  const isCompany = order?.paymentMethodType === PAYMENT_METHOD.PAID_BY_COMPANY;
  const { currency } = order;
  const orderMadeBy = isCompany ? order.owner : order.seller;
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  /**
   * Redirect to /pay page
   */
  const payOrder = () => {
    window.location = `/shop/order/${order._id}/pay`;
  };

  // Check if should render page admin
  const renderPayOrderBtn =
    !isOpportunity &&
    !isAcademy &&
    order?.status === 'pending_payment' &&
    loggedUser?._id === order?.owner?._id &&
    !isCompany &&
    (paymentProviderType === PAYMENT_PROVIDER.STRIPE ||
      paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES);

  // show option to update opportunity status if is the user that created it
  const isOwnerOfOpportunity =
    isOpportunity && order?.seller?._id === loggedUser._id;

  /** region Handle Chat */
  const openChat = useCallback(async () => {
    try {
      setIsLoadingChat(true);
      const { data: response } = await axios.post(
        '/api/chat/group/opportunity',
        {
          opportunity: order?._id,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      ChatService.onOpenSpecificGroup(response.data);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_NO_HELPDESK_SUPPORT');
    } finally {
      setIsLoadingChat(false);
    }
  }, []);

  const classes = cx(
    'btn',
    'm-btn--pill',
    'btn-dynamic',
    'loader-button',
    'btn-pay',
    { 'm-loader': isLoadingChat },
    { 'm-loader--right': isLoadingChat },
    { 'm-loader--light': isLoadingChat },
  );

  /** endregion Handle Chat */

  return (
    <div className="side-tab">
      {orderMadeBy && (
        <SellerInfo t={t} seller={orderMadeBy} isOpportunity={isOpportunity} />
      )}
      {isOwnerOfOpportunity ? (
        <UpdateStatusContainer
          order={order}
          isOpportunity={isOpportunity}
          isLoading={isLoading}
          onSave={onSaveOrder}
        />
      ) : null}
      <MondialRelayWrapper order={order} />
      <TimelineWrapper
        currency={currency}
        order={order}
        isOpportunity={isOpportunity}
      />
      {renderPayOrderBtn ? (
        <Anchor
          onClick={payOrder}
          className="btn m-btn--pill btn-dynamic loader-button btn-pay"
        >
          {t('shop.order.pay')}
        </Anchor>
      ) : null}
      {isOwnerOfOpportunity && order?.hasHelpDeskSupport ? (
        <button
          type="button"
          onClick={openChat}
          className={classes}
          disabled={isLoadingChat}
        >
          {t('see.supportChat')}
        </button>
      ) : null}
    </div>
  );
}

ShopSidebarOrder.propTypes = {
  /** Translation function */
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.func.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  isAcademy: PropTypes.bool,
  isOpportunity: PropTypes.bool,
  onSaveOrder: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  isLoading: PropTypes.bool,
};

ShopSidebarOrder.defaultProps = {
  isAcademy: false,
  isOpportunity: false,
  isLoading: false,
};

export default compose(withCSRF, translate)(ShopSidebarOrder);
